import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import type {
  ContactDto,
  CreateTaskCommand,
  PhaseId,
  TaskDto,
  UpdateTaskCommand,
} from '@/api/generated';
import { useContacts, useCreateTask, useProject, useUpdateTask } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './TaskDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

const schema = z.object({
  title: z.string().trim().min(2, 'Minstens 2 tekens'),
  due: z.string().min(1, 'Kies een datum'),
  assignedContactId: z.string().min(1, 'Kies een verantwoordelijke'),
  notificationOffsetsDays: z.array(z.number().int().min(0)).max(5),
  inheritOffsets: z.boolean(),
  phaseId: z.string().min(1, 'Kies een fase'),
  note: z.string().trim().max(500, 'Maximaal 500 tekens').optional().or(z.literal('')),
  urgent: z.boolean(),
});

type TaskFormValues = z.infer<typeof schema>;

function emptyToNull(v: string | undefined): string | null {
  if (v === undefined) return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
}

/** Parse a comma-separated string of day numbers into a clean number[]. */
function parseOffsets(raw: string): number[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n >= 0);
}

function FieldsBody({
  contacts,
  defaultOffsets,
}: {
  contacts: ContactDto[];
  defaultOffsets: number[];
}) {
  // Watch the inherit toggle so we can hide the manual offsets input while the
  // project default applies. `useWatch` reads the FormProvider context the
  // surrounding <Form> sets up.
  const inheritOffsets = useWatch<TaskFormValues>({ name: 'inheritOffsets' }) as
    | boolean
    | undefined;

  return (
    <>
      <FormField<TaskFormValues> name="title" label="Titel" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            autoFocus
            placeholder="Wat moet er gebeuren?"
          />
        )}
      </FormField>
      <FormField<TaskFormValues> name="due" label="Deadline" required>
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="date"
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      </FormField>
      <FormField<TaskFormValues> name="assignedContactId" label="Verantwoordelijke" required>
        {(field) =>
          contacts.length === 0 ? (
            <div className="mono c-ink-3">
              Nog geen contactpersonen — voeg er eerst één toe bij Contacten.
            </div>
          ) : (
            <select
              {...field}
              className={styles.select}
              value={(field.value as string) ?? ''}
              onChange={(e) => field.onChange(e.target.value)}
            >
              <option value="" disabled>
                Kies een contact…
              </option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.role}
                  {!c.email ? ' (geen e-mail)' : ''}
                </option>
              ))}
            </select>
          )
        }
      </FormField>
      <FormField<TaskFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
      <FormField<TaskFormValues> name="note" label="Notitie">
        {(field) => (
          <Input
            {...field}
            value={(field.value as string | undefined) ?? ''}
            placeholder="Extra context (optioneel)"
          />
        )}
      </FormField>
      <FormField<TaskFormValues> name="inheritOffsets" label="Herinneringen">
        {(field) => (
          <label className={styles.urgentRow}>
            <input
              type="checkbox"
              ref={field.ref}
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
            />
            <span>Projectstandaard gebruiken ({defaultOffsets.join(', ')} dagen vooraf)</span>
          </label>
        )}
      </FormField>
      {!inheritOffsets && (
        <FormField<TaskFormValues> name="notificationOffsetsDays" label="Dagen vooraf">
          {(field) => (
            <Input
              id={field.id}
              name={field.name}
              onBlur={field.onBlur}
              defaultValue={((field.value as number[] | undefined) ?? []).join(', ')}
              placeholder="bv. 7, 1"
              onChange={(e) => field.onChange(parseOffsets(e.target.value))}
            />
          )}
        </FormField>
      )}
      <FormField<TaskFormValues> name="urgent" label="Urgent">
        {(field) => (
          <label className={styles.urgentRow}>
            <input
              type="checkbox"
              ref={field.ref}
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              onBlur={field.onBlur}
            />
            <span>Markeer als urgent</span>
          </label>
        )}
      </FormField>
    </>
  );
}

export function TaskCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateTask();
  const contacts = useContacts({ pageSize: 100 }).data?.items ?? [];
  const defaultOffsets = useProject().data?.taskNotificationDefaultOffsetsDays?.map(Number) ?? [
    7, 1,
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuwe taak</DialogTitle>
        <DialogDescription>Voeg een taak met deadline toe aan het project.</DialogDescription>
        <DialogBody>
          <Form<TaskFormValues>
            schema={schema}
            defaultValues={{
              title: '',
              due: '',
              assignedContactId: '',
              inheritOffsets: true,
              notificationOffsetsDays: defaultOffsets,
              phaseId: defaultPhaseId ?? 'Ruw',
              note: '',
              urgent: false,
            }}
            onSubmit={async (values) => {
              const body: CreateTaskCommand = {
                title: values.title.trim(),
                due: values.due,
                assignedTo: null,
                assignedContactId: values.assignedContactId,
                notificationOffsetsDays: values.inheritOffsets
                  ? null
                  : values.notificationOffsetsDays,
                phaseId: values.phaseId,
                note: emptyToNull(values.note),
                urgent: values.urgent,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody contacts={contacts} defaultOffsets={defaultOffsets} />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function TaskEditDialog({
  open,
  onOpenChange,
  task,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskDto;
}) {
  const updateMutation = useUpdateTask(task.id);
  const contacts = useContacts({ pageSize: 100 }).data?.items ?? [];
  const defaultOffsets = useProject().data?.taskNotificationDefaultOffsetsDays?.map(Number) ?? [
    7, 1,
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Taak bewerken</DialogTitle>
        <DialogDescription>Pas titel, deadline, verantwoordelijke of fase aan.</DialogDescription>
        <DialogBody>
          <Form<TaskFormValues>
            schema={schema}
            defaultValues={{
              title: task.title,
              // <input type="date"> needs yyyy-MM-dd
              due: (task.due ?? '').slice(0, 10),
              assignedContactId: task.assignedContactId ?? '',
              inheritOffsets: task.notificationOffsetsDays == null,
              notificationOffsetsDays: (
                task.notificationOffsetsDays ??
                task.effectiveNotificationOffsetsDays ??
                defaultOffsets
              ).map(Number),
              phaseId: task.phaseId,
              note: task.note ?? '',
              urgent: task.urgent,
            }}
            onSubmit={async (values) => {
              const body: UpdateTaskCommand = {
                title: emptyToNull(values.title),
                due: values.due || null,
                assignedTo: null,
                assignedContactId: values.assignedContactId,
                notificationOffsetsDays: values.inheritOffsets
                  ? null
                  : values.notificationOffsetsDays,
                phaseId: values.phaseId,
                note: emptyToNull(values.note),
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody contacts={contacts} defaultOffsets={defaultOffsets} />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
